import { useFormik } from 'formik';
import { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { Sms } from '@/components/icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import { markSignUpStarted } from '@/lib/analytics';
import { PrivacyPolicyPage, SignInPage, TermsOfUsePage } from '../constants';
import AuthLayout from './AuthLayout';
import { AuthCarousel } from './components/AuthCarousel';
import { SocialLoginRow } from './components/SocialLoginRow';
import type { KeyboardEvent } from 'react';

const signUpSchema = z.object({
    email: z.string().email('E-mail inválido'),
});

type SignUpFormValues = z.infer<typeof signUpSchema>;

export default function SignUp() {
    const { login } = useAuth();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const connectionRef = useRef('');
    const legalLinkState = {
        returnTo: `${location.pathname}${location.search}`,
    };

    const formik = useFormik<SignUpFormValues>({
        initialValues: {
            email: '',
        },
        validate: (values) => {
            const result = signUpSchema.safeParse(values);
            if (!result.success) {
                return result.error.formErrors.fieldErrors;
            }
            return {};
        },
        onSubmit: async () => {
            handleNextStep();
        },
    });

    const handleSocialSubmit = async (connection: string) => {
        connectionRef.current = connection;

        setIsLoading(true);
        try {
            markSignUpStarted();
            await login({
                authorizationParams: { connection },
            });
        } catch (error) {
            console.error('Login error:', error);
            setIsLoading(false);
        }
    };

    const handleNextStep = async () => {
        const errors = await formik.validateForm();
        if (errors.email) {
            formik.setTouched({ email: true });
            return;
        }

        connectionRef.current = 'email';
        setIsLoading(true);

        try {
            markSignUpStarted();
            await login({
                authorizationParams: {
                    login_hint: formik.values.email,
                    screen_hint: 'signup',
                },
            });
        } catch (error) {
            console.error('Login error:', error);
            setIsLoading(false);
        }
    };

    return (
        <AuthLayout leftPanel={<AuthCarousel />}>
            <div className="mx-auto w-full max-w-md">
                <div className="mb-8 text-center">
                    <h2 className="text-2xl font-bold tracking-tight text-foreground">
                        Crie uma conta grátis!
                    </h2>
                </div>

                <div className="space-y-6">
                    <SocialLoginRow
                        onSocialLogin={handleSocialSubmit}
                        isLoading={isLoading}
                        loadingConnection={connectionRef.current}
                    />

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <Separator />
                        </div>
                        <div className="relative flex justify-center text-xs font-medium">
                            <span className="bg-background px-4 text-muted-foreground">
                                Ou entre com seu e-mail
                            </span>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <div className="relative">
                                <Sms className="absolute left-3 top-3 size-6 text-muted-foreground" />
                                <Input
                                    id="email"
                                    placeholder="Digite seu e-mail"
                                    className="h-12 rounded-xl border-border pl-12"
                                    {...formik.getFieldProps('email')}
                                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            handleNextStep();
                                        }
                                    }}
                                />
                            </div>
                            {formik.touched.email && formik.errors.email && (
                                <div className="pl-1 text-xs text-red-500">
                                    {formik.errors.email}
                                </div>
                            )}
                        </div>

                        <Button
                            size="xl"
                            className="w-full"
                            onClick={handleNextStep}
                            disabled={isLoading}
                        >
                            {isLoading && connectionRef.current === 'email'
                                ? 'Redirecionando...'
                                : 'Continuar'}
                        </Button>
                    </div>
                </div>

                <div className="mt-8 text-center text-sm">
                    <p className="text-muted-foreground">
                        Já tem uma conta?{' '}
                        <Link to={SignInPage} className="font-semibold text-brand hover:underline">
                            Entrar
                        </Link>
                    </p>
                    <div className="mt-8 text-xs font-medium text-muted-foreground">
                        Ao continuar, você concorda com os{' '}
                        <Link
                            to={`/${TermsOfUsePage}`}
                            state={legalLinkState}
                            className="underline underline-offset-2 transition-colors hover:text-foreground"
                        >
                            Termos de uso
                        </Link>{' '}
                        e{' '}
                        <Link
                            to={`/${PrivacyPolicyPage}`}
                            state={legalLinkState}
                            className="underline underline-offset-2 transition-colors hover:text-foreground"
                        >
                            Política de privacidade
                        </Link>
                    </div>
                </div>
            </div>
        </AuthLayout>
    );
}
